/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { type IntakeStatus } from '@therapy/shared-validators';
import { Plus } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState, type ChangeEvent, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Button } from '../../components/ui/button.js';
import { Modal } from '../../components/ui/modal.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { Pagination } from '../../components/ui/pagination.js';
import { QueryState } from '../../components/ui/query-state.js';
import { type Client, type ClientsPageParams } from '../../lib/api/resources.js';
import { useClientsPage, useDeleteClient } from '../../lib/queries/clinical.js';
import { useIntakeSummaries } from '../../lib/queries/intake.js';
import { uiStore } from '../../lib/stores/ui-store.js';

import { ClientForm } from './client-form.js';
import { ClientTableRow } from './client-row.js';
import { InviteParentForm } from './invite-parent-form.js';

type StatusFilter = 'all' | Client['status'];
const STATUSES: readonly StatusFilter[] = ['all', 'active', 'inactive', 'discharged'];
const CANCEL = 'common.cancel';
const PAGE_LIMIT = 20;

/** Build the server query params from the page's filter state. */
function pageParams(offset: number, q: string, status: StatusFilter): ClientsPageParams {
  return {
    limit: PAGE_LIMIT,
    offset,
    ...(q !== '' ? { q } : {}),
    ...(status !== 'all' ? { status } : {}),
  };
}

interface ClientsModalsProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly editing: Client | null;
  readonly onFormDone: () => void;
  readonly deleting: Client | null;
  readonly onCancelDelete: () => void;
  readonly onConfirmDelete: () => void;
  readonly deletePending: boolean;
  readonly inviting: Client | null;
  readonly onCloseInvite: () => void;
}

/** The Add/Edit, Delete-confirm, and Invite-parent dialogs for the clients page. */
function ClientsModals(props: ClientsModalsProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      <Modal
        open={props.open}
        onOpenChange={props.onOpenChange}
        title={props.editing !== null ? t('admin.clients.edit') : t('admin.clients.add')}
        description={t('admin.clients.addSubtitle')}
        closeLabel={t(CANCEL)}
      >
        <ClientForm onDone={props.onFormDone} client={props.editing ?? undefined} />
      </Modal>

      <Modal
        open={props.deleting !== null}
        onOpenChange={props.onCancelDelete}
        title={t('admin.clients.deleteTitle')}
        description={t('admin.clients.deleteBody', { name: props.deleting?.fullName ?? '' })}
        closeLabel={t(CANCEL)}
      >
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={props.onCancelDelete}>
            {t(CANCEL)}
          </Button>
          <Button variant="danger" onClick={props.onConfirmDelete} disabled={props.deletePending}>
            {t('common.delete')}
          </Button>
        </div>
      </Modal>

      <Modal
        open={props.inviting !== null}
        onOpenChange={props.onCloseInvite}
        title={t('admin.invite.title')}
        description={t('admin.invite.subtitle')}
        closeLabel={t(CANCEL)}
      >
        {props.inviting !== null && (
          <InviteParentForm client={props.inviting} onDone={props.onCloseInvite} />
        )}
      </Modal>
    </>
  );
}

const Toolbar = observer(function Toolbar({
  status,
  onStatus,
}: {
  readonly status: StatusFilter;
  readonly onStatus: (value: StatusFilter) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const onSearch = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    uiStore.setSearch(e.target.value);
  }, []);
  const onSelect = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      onStatus(e.target.value as StatusFilter);
    },
    [onStatus],
  );
  return (
    <div className="mb-4 flex flex-wrap gap-3">
      <input
        type="search"
        value={uiStore.search}
        onChange={onSearch}
        placeholder={t('admin.clients.searchPlaceholder')}
        aria-label={t('admin.clients.searchPlaceholder')}
        className="focus-ring h-11 flex-1 rounded-xl border border-slate-200 bg-white px-4 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
      />
      <select
        value={status}
        onChange={onSelect}
        aria-label={t('admin.clients.statusFilter')}
        className="focus-ring h-11 rounded-xl border border-slate-200 bg-white px-4 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
      >
        {STATUSES.map((value) => (
          <option key={value} value={value}>
            {value === 'all' ? t('admin.clients.allStatus') : t(`status.${value}`)}
          </option>
        ))}
      </select>
    </div>
  );
});

function ClientsTable({
  rows,
  intakeByClient,
  onView,
  onEdit,
  onDelete,
  onInvite,
}: {
  readonly rows: readonly Client[];
  readonly intakeByClient: ReadonlyMap<string, IntakeStatus>;
  readonly onView: (c: Client) => void;
  readonly onEdit: (c: Client) => void;
  readonly onDelete: (c: Client) => void;
  readonly onInvite: (c: Client) => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
          <tr>
            <th className="px-5 py-3 font-semibold">{t('admin.clients.colName')}</th>
            <th className="px-5 py-3 font-semibold">{t('admin.clients.colDiagnosis')}</th>
            <th className="px-5 py-3 font-semibold">{t('admin.clients.colTherapist')}</th>
            <th className="px-5 py-3 font-semibold">{t('admin.clients.colSessions')}</th>
            <th className="px-5 py-3 font-semibold">{t('admin.clients.colProgress')}</th>
            <th className="px-5 py-3 font-semibold">{t('admin.clients.colStatus')}</th>
            <th className="px-5 py-3 font-semibold">{t('admin.clients.colIntake')}</th>
            <th className="px-5 py-3 font-semibold">{t('admin.clients.colActions')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {rows.map((client) => (
            <ClientTableRow
              key={client.id}
              client={client}
              intakeStatus={intakeByClient.get(client.id) ?? null}
              onView={onView}
              onEdit={onEdit}
              onDelete={onDelete}
              onInvite={onInvite}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Admin → Patients (Figma "Patients" — renamed from Clients, D-A3). */
export const ClientsPage = observer(function ClientsPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [editing, setEditing] = useState<Client | null>(null);
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState<Client | null>(null);
  const [inviting, setInviting] = useState<Client | null>(null);
  const [status, setStatus] = useState<StatusFilter>('all');
  const [offset, setOffset] = useState(0);
  const [debouncedQ, setDebouncedQ] = useState('');
  const search = uiStore.search;
  const page = useClientsPage(pageParams(offset, debouncedQ, status));
  const intakeSummaries = useIntakeSummaries();
  const remove = useDeleteClient();

  const intakeByClient = useMemo<ReadonlyMap<string, IntakeStatus>>(
    () => new Map((intakeSummaries.data ?? []).map((s) => [s.clientId, s.status])),
    [intakeSummaries.data],
  );

  // Debounce the mobx search box into the server query; reset to page 1 on change.
  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedQ(search.trim());
      setOffset(0);
    }, 300);
    return () => {
      clearTimeout(id);
    };
  }, [search]);

  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setEditing(null);
      setOpen(true);
      searchParams.delete('new');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const openAdd = useCallback(() => {
    setEditing(null);
    setOpen(true);
  }, []);
  const closeModal = useCallback(() => {
    setOpen(false);
  }, []);
  const onStatus = useCallback((value: StatusFilter) => {
    setStatus(value);
    setOffset(0);
  }, []);
  const onView = useCallback((c: Client) => void navigate(`/admin/clients/${c.id}`), [navigate]);
  const onEdit = useCallback((c: Client) => {
    setEditing(c);
    setOpen(true);
  }, []);
  const onDelete = useCallback((c: Client) => {
    setDeleting(c);
  }, []);
  const onInvite = useCallback((c: Client) => {
    setInviting(c);
  }, []);
  const closeInvite = useCallback(() => {
    setInviting(null);
  }, []);
  const cancelDelete = useCallback(() => {
    setDeleting(null);
  }, []);
  const confirmDelete = useCallback(() => {
    if (deleting !== null) {
      remove.mutate(deleting.id);
      setDeleting(null);
    }
  }, [deleting, remove]);
  const rows = page.data?.items ?? [];
  const total = page.data?.total ?? 0;

  return (
    <div className="p-8">
      <PageHeader
        title={t('admin.clients.title')}
        subtitle={t('admin.clients.count', { count: total })}
        action={
          <Button onClick={openAdd}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            {t('admin.clients.add')}
          </Button>
        }
      />
      <Toolbar status={status} onStatus={onStatus} />

      <QueryState
        isLoading={page.isLoading}
        isError={page.isError}
        isEmpty={rows.length === 0}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('admin.clients.empty')}
      >
        <ClientsTable
          rows={rows}
          intakeByClient={intakeByClient}
          onView={onView}
          onEdit={onEdit}
          onDelete={onDelete}
          onInvite={onInvite}
        />
        <Pagination total={total} limit={PAGE_LIMIT} offset={offset} onOffsetChange={setOffset} />
      </QueryState>

      <ClientsModals
        open={open}
        onOpenChange={setOpen}
        editing={editing}
        onFormDone={closeModal}
        deleting={deleting}
        onCancelDelete={cancelDelete}
        onConfirmDelete={confirmDelete}
        deletePending={remove.isPending}
        inviting={inviting}
        onCloseInvite={closeInvite}
      />
    </div>
  );
});
